import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowDown, 
  Terminal, 
  Cpu, 
  Server, 
  Cloud, 
  Database,
  Code,
  Zap,
  Lock,
  Globe,
  Sparkles,
  ChevronRight
} from 'lucide-react';
import SineWaveLogo from '../ui/SineWaveLogo';
import TerminalInput from '../ui/TerminalInput';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useParallax } from '../../hooks/useParallax';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const Slide1Portal = () => {
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const gridRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [activeTech, setActiveTech] = useState(0);
  const { currentSlide } = useScrollAnimation();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTech((prev) => (prev + 1) % 6);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const ctx = gsap.context(() => {
      // Floating grid animation - slower on mobile
      if (gridRef.current) {
        gsap.to(gridRef.current, {
          backgroundPosition: isMobile ? '200px 200px' : '500px 500px',
          duration: isMobile ? 30 : 20,
          repeat: -1,
          ease: "linear"
        });
      }

      // Tech icons animation - disable on mobile for performance
      if (!isMobile) {
        gsap.to('.tech-icon', {
          y: -10,
          rotation: 360,
          duration: 4,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
          stagger: 0.5
        });
      }

      // Stats counter animation
      const stats = document.querySelectorAll('.stat-number');
      stats.forEach((stat) => {
        const value = parseInt(stat.textContent || '0');
        gsap.fromTo(stat,
          { textContent: 0 },
          {
            textContent: value,
            duration: 2,
            ease: "power2.out",
            scrollTrigger: {
              trigger: stat,
              start: "top 80%",
              once: true
            },
            onUpdate: function() {
              stat.textContent = Math.floor(this.targets()[0].textContent);
            }
          }
        );
      });

    }, containerRef);

    return () => ctx.revert();
  }, [isMobile]);

  const techStack = [
    { icon: Cpu, name: 'AI/ML', color: 'from-cyan-500 to-blue-500' },
    { icon: Server, name: 'Cloud', color: 'from-blue-500 to-purple-500' },
    { icon: Database, name: 'Data', color: 'from-purple-500 to-pink-500' },
    { icon: Code, name: 'DevOps', color: 'from-green-500 to-teal-500' },
    { icon: Lock, name: 'Security', color: 'from-orange-500 to-red-500' },
    { icon: Globe, name: 'Web3', color: 'from-violet-500 to-indigo-500' }
  ];

  const stats = [
    { value: '7', label: 'Years Expertise', suffix: '+', description: 'In Tech Innovation' },
    { value: '200', label: 'Projects', suffix: '+', description: 'Successfully Delivered' },
    { value: '50', label: 'Clients', suffix: '+', description: 'Global Enterprises' },
    { value: '99.9', label: 'Uptime', suffix: '%', description: 'Service Reliability' }
  ];

  const scrollToNext = () => {
    const nextSection = document.getElementById('solutions');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div 
      ref={containerRef}
      className="relative min-h-screen w-full overflow-x-hidden bg-black mt-20"
    >
      {/* Animated Gradient Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          ref={gridRef}
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: isMobile ? '30px 30px' : '50px 50px',
          }}
        />
        
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900/20 to-black" />
        
        {/* Animated orbs - smaller on mobile */}
        <div className={`absolute top-1/4 left-1/4 ${
          isMobile ? 'w-32 h-32' : 'w-64 h-64'
        } bg-cyan-500/10 rounded-full blur-3xl animate-pulse`} />
        <div className={`absolute bottom-1/4 right-1/4 ${
          isMobile ? 'w-48 h-48' : 'w-96 h-96'
        } bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000`} />
        <div className={`absolute top-3/4 left-3/4 ${
          isMobile ? 'w-16 h-16' : 'w-32 h-32'
        } bg-blue-500/10 rounded-full blur-2xl animate-pulse delay-500`} />
      </div>

      {/* Main Content */}
      <div className="relative z-10 w-full min-h-screen flex flex-col">
        {/* Hero Content */}
        <div className="flex-1 flex flex-col items-center justify-center py-8 sm:py-12 lg:py-24 px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="w-full text-center max-w-7xl mx-auto"
          >
            {/* Main Heading */}
            <motion.div
              ref={titleRef}
              className="mb-6 sm:mb-8 lg:mb-12 px-2"
            >
              <h1 className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 lg:mb-6">
                <span className="block text-gray-300">Build The Future</span>
                <span className="block bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                  With Modern Tech
                </span>
              </h1>
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="text-base sm:text-lg md:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed px-2"
              >
                Enterprise-grade solutions powered by AI, Cloud, and Blockchain.
                Transforming businesses with cutting-edge technology.
              </motion.p>
            </motion.div>

            {/* Tech Stack Carousel */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="mb-6 sm:mb-8 lg:mb-12 px-2"
            >
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-6 px-2">
                {techStack.map((tech, index) => (
                  <motion.div
                    key={tech.name}
                    className={`tech-icon flex flex-col items-center p-2 sm:p-3 rounded-xl backdrop-blur-sm border transition-all duration-300 ${
                      activeTech === index
                        ? 'border-cyan-500/50 bg-gradient-to-b from-gray-900/50 to-black/50 scale-105 sm:scale-110'
                        : 'border-gray-700/50 bg-gray-900/30'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    style={{ minWidth: isMobile ? '70px' : 'auto' }}
                  >
                    <tech.icon className={`${
                      isMobile ? 'w-5 h-5' : 'w-6 h-6 sm:w-8 sm:h-8'
                    } mb-1 sm:mb-2 ${
                      activeTech === index 
                        ? 'text-cyan-400' 
                        : 'text-gray-400'
                    }`} />
                    <span className={`${
                      isMobile ? 'text-xs' : 'text-xs sm:text-sm'
                    } font-medium ${
                      activeTech === index 
                        ? 'text-cyan-300' 
                        : 'text-gray-500'
                    }`}>
                      {tech.name}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Terminal Section */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="w-full mb-6 sm:mb-8 lg:mb-12 px-4"
            >
              <div className="relative max-w-2xl mx-auto">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl blur opacity-20 sm:opacity-30"></div>
                <div className="relative bg-gray-900/90 backdrop-blur-sm rounded-xl p-3 sm:p-4 md:p-6 border border-gray-700/50">
                  <TerminalInput
                    placeholder="Enter command (try: 'show services', 'demo ai', 'contact team')"
                    onSubmit={(cmd) => console.log('Command:', cmd)}
                    className="text-sm sm:text-base"
                  />
                </div>
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 sm:mb-12 lg:mb-16 px-4"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group relative px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-lg font-semibold text-white overflow-hidden w-full sm:w-auto"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-700 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative flex items-center justify-center gap-2 sm:gap-3">
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-sm sm:text-base">Start Free Trial</span>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={scrollToNext}
                className="group px-6 py-3 sm:px-8 sm:py-4 bg-gray-900/50 border border-gray-700 rounded-lg font-semibold text-gray-300 hover:border-cyan-500/50 hover:text-cyan-300 transition-all w-full sm:w-auto"
              >
                <div className="flex items-center justify-center gap-2 sm:gap-3">
                  <span className="text-sm sm:text-base">View Solutions</span>
                  <ArrowDown className="w-4 h-4 sm:w-5 sm:h-5 group-hover:animate-bounce" />
                </div>
              </motion.button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 1 }}
              className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 px-2"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -3 }}
                  className="group relative bg-gray-900/30 backdrop-blur-sm border border-gray-700/30 rounded-xl p-3 sm:p-4 md:p-6 hover:border-cyan-500/50 transition-all"
                >
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-1 sm:mb-2">
                      <span className="stat-number bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                        {stat.value}
                      </span>
                      <span className="text-white">{stat.suffix}</span>
                    </div>
                    <div className="text-xs sm:text-sm font-semibold text-cyan-300 mb-0.5 sm:mb-1">
                      {stat.label}
                    </div>
                    <div className="text-xs text-gray-500 group-hover:text-gray-400 transition-colors leading-tight">
                      {stat.description}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="border-t border-gray-800/50 py-4 px-4"
        >
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0 max-w-7xl mx-auto">
            <div className="flex flex-wrap justify-center sm:justify-start items-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-500">
              <span className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Systems: <span className="text-green-400">Online</span></span>
              </span>
              <span>API: <span className="text-cyan-300">99.9% Uptime</span></span>
              <span>Support: <span className="text-blue-300">24/7</span></span>
            </div>

            <div className="flex items-center space-x-3 sm:space-x-4">
              <button className="text-gray-400 hover:text-cyan-300 transition-colors text-xs sm:text-sm">
                Privacy
              </button>
              <button className="text-gray-400 hover:text-cyan-300 transition-colors text-xs sm:text-sm">
                Terms
              </button>
              <button className="text-gray-400 hover:text-cyan-300 transition-colors text-xs sm:text-sm">
                Docs
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Floating Tech Elements - Hidden on small screens */}
      {!isMobile && (
        <>
          <div className="absolute top-20 left-10 opacity-20">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <div className="w-32 h-32 border border-cyan-500/20 rounded-full"></div>
            </motion.div>
          </div>

          <div className="absolute bottom-20 right-10 opacity-20">
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            >
              <div className="w-40 h-40 border border-blue-500/20 rounded-full"></div>
            </motion.div>
          </div>
        </>
      )}
    </div>
  );
};

export default Slide1Portal;